import type {RepositoryNWO} from '@github-ui/current-repository'
import {DiffStats} from '@github-ui/diffs/DiffParts'
import {commitPath, repositoryTreePath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {FileCodeIcon} from '@primer/octicons-react'
import {Button, IconButton, Label, Link as PrimerLink, PageHeader} from '@primer/react'
import {Tooltip} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {memo, useRef} from 'react'

import {useCommitsAppPayload} from '../../../hooks/use-commits-app-payload'
import type {BranchCommitState} from '../../../hooks/use-load-branch-commits'
import {useLoadSingleDeferredCommitData} from '../../../shared/use-load-deferred-commit-data'
import type {CommitExtended, HeaderInfo} from '../../../types/commit-types'
import {shortSha} from '../../../utils/short-sha'
import {CommitAttribution, PushAttribution} from '../../CommitAttribution'
import {CopySHA} from '../../CopySHA'
import {DeferredCommitHeaderData} from '../../DeferredCommitHeaderData'
import {Panel} from '../../Panel'
import {CommitBranchInfo} from './CommitBranchInfo'
import styles from './CommitHeader.module.css'
import {CommitParents} from './CommitParents'
import {CommitTagInfo} from './CommitTagInfo'

interface CommitHeaderProps {
  commit: CommitExtended
  commitInfo: BranchCommitState
  repo: RepositoryNWO
  diffsHeader?: HeaderInfo
}

export const CommitHeader = memo(CommitHeaderUnmemoized)

function CommitHeaderUnmemoized({commit, commitInfo, repo, diffsHeader}: CommitHeaderProps) {
  const commitSubjectRef = useRef<HTMLSpanElement>(null)
  const {helpUrl} = useCommitsAppPayload()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const showPusherOnCommitDetail = useFeatureFlag('show_pusher_on_commit_detail')
  const deferredData = useLoadSingleDeferredCommitData(
    `${commitPath({owner: repo.ownerLogin, repo: repo.name, commitish: commit.oid})}/deferred_commit_data`,
  )

  return (
    <PageHeader>
      <PageHeader.TitleArea variant="large" className={styles.PageHeader_TitleArea}>
        <PageHeader.Title as="h1" className={clsx('f2', styles.PageHeader_Title)}>
          Commit <span className="text-mono bgColor-muted rounded p-1">{shortSha(commit.oid)}</span>
        </PageHeader.Title>
      </PageHeader.TitleArea>
      <PageHeader.Actions className={clsx(styles['commit-header-actions'])}>
        <div className="d-flex gap-2 flex-items-center flex-md-justify-end">
          <Feedback />

          <Tooltip
            text="Browse the repository at this point in the history"
            direction="sw"
            className={clsx('d-none d-md-flex', styles.browseFilesTooltip)}
          >
            <Button
              as={PrimerLink}
              href={repositoryTreePath({repo, action: 'tree', commitish: commit.oid})}
              leadingVisual={FileCodeIcon}
              className={styles.browseFilesButton}
            >
              Browse files
            </Button>
          </Tooltip>

          <IconButton
            as={PrimerLink}
            href={repositoryTreePath({repo, action: 'tree', commitish: commit.oid})}
            icon={FileCodeIcon}
            aria-label="Browse files"
            className="d-flex d-md-none"
          />
        </div>
      </PageHeader.Actions>
      <PageHeader.Description>
        <div className="d-flex flex-column gap-2 width-full">
          <CommitAttribution
            commit={commit}
            repo={repo}
            settings={{
              fontColor: 'var(--fgColor-default)',
              avatarSize: 20,
              fontWeight: 'var(--base-text-weight-semibold)',
            }}
            textVariant="muted"
          >
            <DeferredCommitHeaderData deferredData={deferredData} oid={commit.oid} repo={repo} helpUrl={helpUrl} />
          </CommitAttribution>

          <Panel className="pt-0">
            {(commit.shortMessageMarkdown || commit.bodyMessageHtml) && (
              <div className={styles.commitMessageContainer}>
                {commit.shortMessageMarkdown && (
                  <VerifiedHTMLText
                    ref={commitSubjectRef}
                    html={commit.shortMessageMarkdown}
                    className="ws-pre-wrap f5 wb-break-word text-mono"
                  />
                )}
                {commit.bodyMessageHtml && (
                  <VerifiedHTMLText
                    html={commit.bodyMessageHtml}
                    className="ws-pre-wrap extended-commit-description-container f6 wb-break-word text-mono mt-2"
                  />
                )}
              </div>
            )}

            <div
              className={styles.commitBranchContainer}
              data-files-changed-active={robustSmallScreenSupport && diffsHeader ? true : undefined}
            >
              <div className="d-flex flex-row flex-items-center">
                <CommitBranchInfo data={commitInfo} repo={repo} />
                {commitInfo.tags.length > 0 && <span className="px-2">&middot;</span>}
                <CommitTagInfo data={commitInfo} repo={repo} />
              </div>

              <pre className="color-fg-muted d-flex flex-items-center">
                <CommitParents commit={commit} repo={repo} />
                {' commit '}
                <span className="fgColor-default">{shortSha(commit.oid)}</span>
                <CopySHA className="ml-2" sha={commit.oid} direction="sw" />
              </pre>
            </div>

            {robustSmallScreenSupport && diffsHeader && (
              <div className={styles.commitFilesChangedContainer}>
                <div>
                  <p className="text-bold mb-0">
                    {diffsHeader.filesChangedString} file{diffsHeader.filesChanged > 1 ? 's' : ''} changed
                  </p>
                </div>
                <DiffStats
                  linesAdded={diffsHeader.additions}
                  linesDeleted={diffsHeader.deletions}
                  linesChanged={diffsHeader.additions + diffsHeader.deletions}
                />
              </div>
            )}

            {showPusherOnCommitDetail && commit.pusher && (
              <div className={clsx(styles['pusher-container'])}>
                <PushAttribution commit={commit} repo={repo} textVariant="muted" />
              </div>
            )}
          </Panel>
        </div>
      </PageHeader.Description>
    </PageHeader>
  )
}

function Feedback() {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const diffUXRefreshBeta = useFeatureFlag('diff_ux_refresh_beta')

  if (!diffUXRefreshBeta) {
    return null
  }

  return (
    <div className="d-flex flex-items-center gap-2">
      <Label variant="success">Preview</Label>
      <PrimerLink
        href="https://gh.io/new-commit-details-feedback"
        target="_blank"
        rel="noopener noreferrer"
        className="no-wrap f5 text-normal d-none d-sm-block"
      >
        Give feedback
      </PrimerLink>
    </div>
  )
}
